import { isRequestError } from 'utils/requestError';
import './TransferBIDVFast.scss';
import {
  InputSearchSelect,
  InputText,
  InputNumber,
  Loading,
  ModalPinCode,
} from 'components';
import { Button, InputWraper, useForm } from 'modules';
import React, { useRef, useState } from 'react';
import * as Yup from 'yup';
import { toast } from 'react-hot-toast';
import {
  useBidvSubmitTransferMutation,
  useBidvLookupAccountQuery,
  useListBidvDomesticBanksQuery,
  useCheckPinCodeMutation,
} from 'services';
import { SubmitTransferData } from 'services/openapi';
import BidvTransferResultModal from '../components/BidvTransferResultModal';

const TransferBIDVFast = () => {
  const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
  const [transferResult, setTransferResult] =
    useState<SubmitTransferData | null>(null);
  const pinCodeRef = useRef('');

  const { mutateAsync: mutateAsyncSubmit, isLoading: isLoadingSubmit } =
    useBidvSubmitTransferMutation();
  const {
    mutateAsync: mutateAsyncCheckPinCode,
    isLoading: isLoadingCheckPinCode,
  } = useCheckPinCodeMutation();

  const { data: domesticBanks } = useListBidvDomesticBanksQuery({
    transferMethod: 'F',
    limit: 200,
  });

  const {
    handleSubmit,
    getInputProps,
    resetForm,
    setValues,
    validateForm,
    isAllFilled,
    getValue: getFormValue,
  } = useForm({
    structure: [
      {
        name: 'amount',
        label: 'Số tiền',
        validate: Yup.number()
          .typeError('Vui lòng nhập số tiền hợp lệ')
          .min(2000, 'Số tiền phải lớn hơn 2000')
          .required('Vui lòng nhập số tiền'),
      },
      {
        name: 'bankCode',
        label: 'Ngân hàng thụ hưởng',
        validate: Yup.string().required('Vui lòng chọn ngân hàng'),
      },
      {
        name: 'beneficiaryAccount',
        label: 'Số tài khoản',
        validate: Yup.string().required('Vui lòng nhập số tài khoản'),
      },
      {
        name: 'beneficiaryName',
        label: 'Tên tài khoản',
        validate: Yup.string().required('Vui lòng nhập tên tài khoản'),
        isDisabled: true,
      },
      {
        name: 'remark',
        label: 'Nội dung',
        validate: Yup.string().required('Vui lòng nhập nội dung'),
      },
    ],
    onSubmit: async (values) => {
      try {
        const res = await mutateAsyncSubmit({
          transferMethod: 'F',
          beneficiaryAccount: values.beneficiaryAccount?.trim(),
          beneficiaryName: values.beneficiaryName?.trim(),
          bankCode: values.bankCode,
          amount: Number(values.amount),
          remark: values.remark?.trim(),
          pinCode: pinCodeRef.current,
        });
        setTransferResult(res?.data ?? null);
        setValues({
          amount: 0,
          beneficiaryAccount: '',
          beneficiaryName: '',
          remark: '',
        });
        resetForm();
      } catch (error) {
        console.error('Submit error:', error);
      }
    },
  });

  const { isFetching: isFetchingLookupAccount, refetch: lookupAccount } =
    useBidvLookupAccountQuery(
      {
        transferMethod: 'F',
        accountNo: getFormValue('beneficiaryAccount')?.trim() || '',
        bankCode: getFormValue('bankCode') || '',
      },
      false,
    );

  const handleLookupAccount = async () => {
    const bankCode = getFormValue('bankCode');
    const account = getFormValue('beneficiaryAccount');

    if (!bankCode) {
      toast.error('Vui lòng chọn ngân hàng trước');
      return;
    }
    if (!account?.trim()) {
      toast.error('Vui lòng nhập số tài khoản');
      return;
    }
    try {
      setValues({ beneficiaryName: '' });
      const result = await lookupAccount();
      if (result.isError) return;
      if (result.data?.data?.accountName) {
        setValues({ beneficiaryName: result.data.data.accountName });
        toast.success('Đã tìm thấy thông tin tài khoản', { duration: 1500 });
      } else {
        toast.error('Không tìm thấy thông tin tài khoản');
      }
    } catch (error) {
      console.error('Lookup error:', error);
      if (!isRequestError(error)) {
        toast.error('Không tìm thấy thông tin tài khoản');
      }
    }
  };

  async function handleCheckPinCode(pinCode: string) {
    try {
      const result = await mutateAsyncCheckPinCode({ pinCode });
      if (!result?.isValid) {
        result?.message?.forEach((msg: string) => toast.error(msg));
      }
      setIsShowPopup(false);
      if (result?.isValid) {
        pinCodeRef.current = pinCode;
        await handleSubmit();
      }
    } catch (error) {
      setIsShowPopup(false);
      console.error(error);
    }
  }

  const bankOptions = domesticBanks?.data?.items?.map((item) => ({
    label: `${item.name} (${item.shortName})`,
    value: item.code,
    logo: item.bankLogoUrl,
  }));

  return (
    <div className='bidv-transfer'>
      <Loading isLoading={isLoadingSubmit}>
        <div className='row container-fluid-bidv-transfer'>
          <h1>Chuyển khoản nhanh 247</h1>
          <InputWraper
            isRequired
            className='col-lg-12'
            inputProps={getInputProps('amount')}
            component={InputNumber}
          />
          <InputWraper
            isRequired
            className='col-lg-12'
            inputProps={getInputProps('bankCode')}
            renderInput={(props) => (
              <InputSearchSelect
                {...props}
                placeholder='Chọn ngân hàng'
                title='Chọn ngân hàng'
                onChange={(e) => {
                  setValues({
                    bankCode: e,
                    beneficiaryName: '',
                    beneficiaryAccount: '',
                  });
                  props.onChange(e);
                }}
                options={bankOptions}
              />
            )}
          />
          <InputWraper
            isRequired
            className='col-lg-12 bidv-transfer__account-lookup'
            inputProps={{
              ...getInputProps('beneficiaryAccount'),
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLookupAccount();
                }
              },
            }}
            renderInput={(props) => (
              <>
                <InputText {...props} />
                <Button
                  isVisible
                  label='Kiểm tra'
                  type='button'
                  buttonType='primary'
                  isLoading={isFetchingLookupAccount}
                  disabled={isFetchingLookupAccount}
                  onClick={handleLookupAccount}
                />
              </>
            )}
          />
          <InputWraper
            isRequired
            className='col-lg-12'
            inputProps={getInputProps('beneficiaryName')}
            component={InputText}
          />
          <InputWraper
            isRequired
            className='col-lg-12'
            inputProps={getInputProps('remark')}
            component={InputText}
          />
        </div>

        <p className='text-danger'>
          * Chúng tôi không chịu trách nhiệm cho việc điền sai thông tin nhận
          tiền hoặc bị hack tài khoản
        </p>
        <div className='wrap-button-create-user'>
          <Button
            label='Chuyển tiền'
            type='submit'
            disabled={isLoadingSubmit || !isAllFilled()}
            onClick={() => {
              validateForm().then((errors: any) => {
                if (Object.keys(errors).length > 0) {
                  Object.keys(errors).forEach((key) =>
                    toast.error(errors[key]),
                  );
                  return;
                }
                setIsShowPopup(true);
              });
            }}
            buttonType='success'
          />
        </div>
      </Loading>

      {transferResult && (
        <BidvTransferResultModal
          submitData={transferResult}
          onClose={() => setTransferResult(null)}
        />
      )}

      <ModalPinCode
        isOpen={isShowPopup}
        isLoading={isLoadingCheckPinCode}
        onClose={() => setIsShowPopup(false)}
        onConfirm={handleCheckPinCode}
      />
    </div>
  );
};

export default TransferBIDVFast;
